import { Alert } from 'components/ui/alert';
import { ListBoxInput } from 'components/Inputs/ListBoxInput';
import { Input } from 'components/ui/input';
import { LoadingMessage } from 'components/Loader/LoadingMessage';
import { defaultFunctions, defaultVariables } from 'equation-resolver';
import { IndicatorVariable } from 'interfaces/indicator';
import React, { ChangeEvent, FC, useEffect, useState } from 'react';
import { EquationContext, EquationOptions } from 'react-equation';
import { useQuery } from 'react-query';
import { customErrorHandler } from './ErrorHandler';
import { useService } from 'hooks/useService';
import { ContentType } from 'services/abstract';
const VariableList: FC<{ variables: IndicatorVariable[] }> = ({
  variables,
}) => {
  return variables.length ? (
    <Alert variant="info">
      <h3 className="underline">Variables :</h3>
      <ul className="italic">
        {variables.map(({ columnName, alias }) => {
          return (
            <li key={alias}>
              <b>{alias} = </b>
              {columnName}
            </li>
          );
        })}
      </ul>
    </Alert>
  ) : (
    <Alert variant="destructive">
      {`Veuillez d'abord sélectionner des colonnes`}
    </Alert>
  );
};

type EquationInputProps = {
  variables: IndicatorVariable[];
  value?: string;
  onChange: (value: string) => void;
};

const FALLBACK_FORMULA = 'A-1';

const EquationInput = React.forwardRef<HTMLDivElement, EquationInputProps>(
  ({ variables, value, onChange }, ref) => {
    const [formula, setFormula] = useState(value ?? FALLBACK_FORMULA);
    const equationService = useService(ContentType.EQUATION);
    const { data: response, isLoading } = useQuery({
      queryKey: ['equations'],
      queryFn: async () => {
        return await equationService.getEquations();
      },
      keepPreviousData: true,
    });

    const options = response?.data ?? [];
    const defaultOptionValue = options.length ? options[0].value : undefined;
    const listBoxDefaultValue = formula;

    useEffect(() => {
      if (value !== undefined && value !== formula) {
        setFormula(value);
      }
    }, [value, formula]);

    useEffect(() => {
      if (!defaultOptionValue || value !== undefined) {
        return;
      }
      setFormula(defaultOptionValue);
      onChange(defaultOptionValue);
    }, [defaultOptionValue, onChange, value]);

    const handleEquationSelect = (selectedFormula: string) => {
      setFormula(selectedFormula);
      onChange(selectedFormula);
    };

    const handleEquationUpdate = (event: ChangeEvent<HTMLInputElement>) => {
      const updatedFormula = event.target.value;
      setFormula(updatedFormula);
      onChange(updatedFormula);
    };

    const equationVariables = variables.reduce((acc, v) => {
      acc[v.alias] = {
        type: 'number',
        value: v.sample,
      };
      return acc;
    }, defaultVariables);

    if (isLoading) {
      return <LoadingMessage />;
    }

    return (
      <div ref={ref}>
        <VariableList variables={variables} />
        <ListBoxInput
          className="mb-2"
          defaultValue={listBoxDefaultValue}
          options={options}
          onChange={handleEquationSelect}
        />
        <Input
          data-testid="equation-input"
          id="equation"
          className="w-full"
          onChange={handleEquationUpdate}
          value={formula}
        />
        <EquationOptions
          variables={equationVariables}
          functions={defaultFunctions}
          errorHandler={customErrorHandler}
        >
          <EquationContext
            render={(equation) => (
              <div>
                <div className="text-2xl">Exemple :</div>
                {equation(`${formula}=`)}
              </div>
            )}
          />
        </EquationOptions>
      </div>
    );
  }
);

EquationInput.displayName = 'EquationInput';

export default EquationInput;
